import {
  CandidateRow,
  ElementOriginMapping,
  ProjectElementImport,
} from '@shared/models/element-import.model';
import { Baseline, VersionImports } from '@shared/models/project.model';
import { ElementQuantityExpressionName } from '@shared/models/element-quantities.model';
import {
  ProjectChange,
  ProjectChangeType,
} from '@shared/helpers/pipeline/update-engine.models';
import { tryMaterializeCandidateRow } from '@shared/helpers/materialize-candidate.helpers';
import type { IfcMeasure } from '@shared/helpers/ifc-quantity.helpers';
import {
  AutoDiscardEntry,
  buildVersionImportsChange,
  getDiscardedRowIds,
} from '@shared/helpers/candidate-discard.helpers';
import {
  ProductsById,
  RecipesById,
  collectImportedElementsInView,
} from './candidate-status.helpers';
import { MappingsByCode } from './element-imports.store';

export type CollectImportResult = {
  changes: ProjectChange[];
  failedRows: CandidateRow[];
  /**
   * Rows that could not materialize on a file drop but whose resource code
   * carries a product mapping (DEV-3361): candidate-row id → product id. The
   * caller seeds a product-only draft so the row's in-row form shows the
   * mapped product and the user only has to finish its unit conversion.
   */
  productPrefillByRowId: Record<string, string>;
};

/**
 * Build the import changes for every candidate row that can be imported right
 * now: has a complete resource-code mapping, is not discarded, and is not
 * already materialized in the version.
 *
 * On a batch file drop (`trigger: 'auto'`), a row whose code carries a saved
 * discard mapping (DEV-3360) is auto-discarded instead of added: if it also has
 * a complete product mapping it is materialized and the element is *stashed*
 * (so Restore is lossless → AUTO-MAPPED), otherwise it is discarded with no
 * stash (Restore → INCOMPLETE). User-driven single-row imports keep
 * `trigger: 'user'` and skip this branch.
 */
export const collectImportChanges = (
  rows: readonly CandidateRow[],
  mappingsByCode: MappingsByCode,
  productsById: ProductsById,
  recipesById: RecipesById,
  version: Baseline,
  importsById: Record<string, ProjectElementImport>,
  draftMappingsByRowId:
    Record<string, ElementOriginMapping | undefined> | undefined,
  quantityOverridesByRowId: Record<string, number | undefined> | undefined,
  selectedQuantityByRowId:
    Record<string, ElementQuantityExpressionName | undefined> | undefined,
  trigger: 'auto' | 'user',
  /**
   * When set (file upload), the import's id is appended to the version's
   * `importIds` inside the same {@link VersionImports} change as the auto-discard
   * and prefill drafts — one imports change per batch so the whole upload is a
   * single undo frame.
   */
  newImportId?: string,
  /** Per-row IFC measure override so Quantity/Unit match the user's pick. */
  ifcMeasureByRowId?: Record<string, IfcMeasure | undefined>,
): CollectImportResult => {
  const importedElements = collectImportedElementsInView(version);
  const discardedRowIds = new Set(getDiscardedRowIds(version));
  const changes: ProjectChange[] = [];
  const failedRows: CandidateRow[] = [];
  const productPrefillByRowId: Record<string, string> = {};
  const autoDiscard: AutoDiscardEntry[] = [];
  const materializedRowIds: string[] = [];

  for (const row of rows) {
    if (discardedRowIds.has(row.id)) continue;
    if (importedElements.has(row.id)) continue;

    const discardMapped =
      trigger === 'auto' &&
      Boolean(row.resourceCode && mappingsByCode[row.resourceCode]?.discard);

    try {
      const result = tryMaterializeCandidateRow(row, {
        mappingsByCode,
        productsById,
        recipesById,
        importsById,
        draftMappingsByRowId,
        quantityOverridesByRowId,
        selectedQuantityByRowId,
        ifcMeasureByRowId,
        trigger,
      });

      if (discardMapped) {
        // Materialize only to stash; the element never enters the tree.
        autoDiscard.push({
          rowId: row.id,
          stashed: result.ok
            ? { element: result.element, parentId: version.id }
            : undefined,
        });
        continue;
      }

      if (!result.ok) {
        // Couldn't materialize (e.g. the row's CSV unit isn't covered by the
        // saved conversion). Still surface the mapped product so the row is
        // pre-filled — only on a batch file drop, from the row's own code
        // mapping (never a draft, so nothing leaks across rows).
        const mapping = row.resourceCode
          ? mappingsByCode[row.resourceCode]
          : undefined;
        if (
          trigger === 'auto' &&
          mapping?.referenceType === 'product' &&
          mapping.referenceId &&
          !mapping.discard
        ) {
          productPrefillByRowId[row.id] = mapping.referenceId;
        }
        continue;
      }

      changes.push({
        type: ProjectChangeType.ElementAdd,
        elementId: version.id,
        factory: result.element,
        generateUniqueName: false,
      });
      materializedRowIds.push(row.id);
    } catch (error) {
      console.error(
        `Failed to materialize candidate row "${row.name ?? row.resourceCode}":`,
        error,
      );
      failedRows.push(row);
    }
  }

  // Fold auto-discards AND product-prefill drafts into a single VersionImports
  // change: drafts live on the project now, so two separate imports changes in
  // one batch would clobber each other (each replaces the whole imports object).
  const discardedRows = { ...(version.imports?.discardedRows ?? {}) };
  let discardedChanged = false;
  for (const { rowId, stashed } of autoDiscard) {
    if (discardedRows[rowId]) continue;
    discardedRows[rowId] = { type: 'auto', stashed };
    discardedChanged = true;
  }

  const draftsByRowId = { ...(version.imports?.draftsByRowId ?? {}) };
  let draftsChanged = false;
  for (const [rowId, productId] of Object.entries(productPrefillByRowId)) {
    draftsByRowId[rowId] = {
      ...(draftsByRowId[rowId] ?? {}),
      referenceId: productId,
      referenceType: 'product',
    };
    draftsChanged = true;
  }
  // A materialized row's mapping now lives on the element's origin — drop its
  // draft in the same batch so the import is a single undo frame (undo brings
  // the draft back together with removing the element). Replaces the old
  // clear-on-mount effect, which pushed a separate frame after the import.
  for (const rowId of materializedRowIds) {
    const existing = draftsByRowId[rowId];
    if (existing === undefined) continue;
    const keptMeasure = existing.ifcMeasure ?? ifcMeasureByRowId?.[rowId];
    if (keptMeasure) {
      draftsByRowId[rowId] = { ifcMeasure: keptMeasure };
    } else {
      delete draftsByRowId[rowId];
    }
    draftsChanged = true;
  }

  const patch: Partial<VersionImports> = {};
  if (newImportId) {
    patch.importIds = [...(version.imports?.importIds ?? []), newImportId];
  }
  if (discardedChanged) patch.discardedRows = discardedRows;
  if (draftsChanged) patch.draftsByRowId = draftsByRowId;
  if (Object.keys(patch).length > 0) {
    changes.push(buildVersionImportsChange(version, patch));
  }

  return { changes, failedRows, productPrefillByRowId };
};
